import { auraDef, type CcType, type School } from '@arena/shared';
import type { AuraView } from './hud.svelte';

export type LossKind = CcType | 'lockout';

export interface LossOfControl {
  kind: LossKind;
  /** What happened to you: "Stunned", "Fire Locked". */
  label: string;
  /** What did it: the aura's name, or "Interrupted". */
  name: string;
  icon: string;
  remaining: number;
  duration: number;
  /** Can't act at all: stunned, feared or incapacitated. */
  full: boolean;
}

const LABELS: Record<CcType, string> = {
  stun: 'Stunned',
  fear: 'Feared',
  incapacitate: 'Incapacitated',
  frozen: 'Frozen',
  silence: 'Silenced',
  root: 'Rooted',
};

/** Lower shows first; within a rank the longest wins. */
const RANK: Record<LossKind, number> = {
  stun: 0,
  fear: 0,
  incapacitate: 0,
  frozen: 0,
  silence: 1,
  lockout: 1,
  root: 2,
};

/** The one effect the loss-of-control banner shows, if any. */
export function lossOfControl(
  auras: AuraView[],
  lockouts: { school: School; remaining: number; duration: number }[],
  schoolIcon: (school: School) => string,
): LossOfControl | null {
  const shown: LossOfControl[] = [
    ...auras.flatMap((a) => {
      const cc = auraDef(a.auraId).cc;
      if (!a.harmful || !cc) return [];
      return [
        {
          kind: cc,
          label: LABELS[cc],
          name: a.name,
          icon: a.icon,
          remaining: a.remaining,
          duration: a.duration,
          full: RANK[cc] === 0,
        },
      ];
    }),
    ...lockouts
      .filter((l) => l.remaining > 0)
      .map((l) => ({
        kind: 'lockout' as const,
        label: `${l.school[0].toUpperCase()}${l.school.slice(1)} Locked`,
        name: 'Interrupted',
        icon: schoolIcon(l.school),
        remaining: l.remaining,
        duration: Math.max(l.duration, l.remaining),
        full: false,
      })),
  ];
  shown.sort(
    (a, b) => RANK[a.kind] - RANK[b.kind] || b.remaining - a.remaining,
  );
  return shown[0] ?? null;
}
